import {
  editCostCenterFormSchema,
  EditCostCenterFormData,
  defaultEditFormValues,
} from '@pages/cost-centers/utils';
import { ErrorDetailsDtoErrorCodeEnum, ResponseErrorDto } from '@interfaces/models';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { editCostCenter, getCostCenter } from '@api/cost-centers';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from '@ui/components';

export const useEditCostCenterDrawer = ({
  onSuccess,
  id,
}: {
  onSuccess: () => void;
  id?: string;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const form = useForm({
    defaultValues: defaultEditFormValues,
    resolver: zodResolver(editCostCenterFormSchema(t)),
    mode: 'onBlur',
  });

  const {
    data: costCenter,
    isLoading: isCostCenterLoading,
    refetch,
  } = useQuery({
    queryKey: ['cost-center', id],
    queryFn: () => getCostCenter(id!),
    enabled: !!id,
  });

  const { mutate: editCostCenterMutation, isPending } = useMutation({
    mutationFn: editCostCenter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-centers'] });
      queryClient.invalidateQueries({ queryKey: ['cost-center'] });
      refetch();
      onSuccess();
      toast.success(t('COST_CENTERS.EDIT_COST_CENTER_SUCCESS'));
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      if (error.errorCode === ErrorDetailsDtoErrorCodeEnum.COST_CENTER_NAME_ALREADY_EXISTS) {
        form.setError('name', {
          message: t(`API_ERRORS.${ErrorDetailsDtoErrorCodeEnum.COST_CENTER_NAME_ALREADY_EXISTS}`),
          type: 'custom',
        });
        return;
      }
      return toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const editCostCenterHandler = (data: EditCostCenterFormData) => {
    editCostCenterMutation({ id: id!, data });
  };

  const onSubmit = form.handleSubmit(editCostCenterHandler);

  useEffect(() => {
    if (costCenter) {
      form.reset({
        ...costCenter,
        wasInitiallyDefault: costCenter.defaultCostCenter,
        wasInitiallyAutomaticAssignment: costCenter.automaticAssignment,
      });
    }
  }, [costCenter, form]);

  const isDefaultCostCenter = useMemo(() => costCenter?.defaultCostCenter, [costCenter]);

  return { onSubmit, form, isPending, isCostCenterLoading, isDefaultCostCenter };
};
